'use client';

import { useEffect, useState, useRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { authService } from '../services/auth';
import { isTestingMode } from '../config';
import { debugLog } from '../utils/debugLogger';

/**
 * Custom hook for authentication protection
 * @param {Object} options - Configuration options
 * @param {boolean} options.requireAuth - Whether this page requires authentication (default: true)
 * @param {string} options.redirectTo - Where to redirect if not authenticated (default: '/login')
 * @param {string} options.redirectIfAuthenticated - Where to redirect if already authenticated (for login page)
 */
export const useAuthGuard = (options = {}) => {
  const {
    requireAuth = true,
    redirectTo = '/login',
    redirectIfAuthenticated = null,
  } = options;

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const router = useRouter();
  const pathname = usePathname();

  // Track if auth check has been performed to prevent duplicate calls
  const authCheckPerformed = useRef(false);
  const lastConfig = useRef({ requireAuth, redirectTo, redirectIfAuthenticated, pathname });

  useEffect(() => {
    // Check if configuration changed
    const configChanged =
      lastConfig.current.requireAuth !== requireAuth ||
      lastConfig.current.redirectTo !== redirectTo ||
      lastConfig.current.redirectIfAuthenticated !== redirectIfAuthenticated ||
      lastConfig.current.pathname !== pathname;

    // Only run if config changed or first time
    if (!authCheckPerformed.current || configChanged) {
      lastConfig.current = { requireAuth, redirectTo, redirectIfAuthenticated, pathname };
      authCheckPerformed.current = true;

      const checkAuth = () => {
        try {
          // Debug authentication check - only in non-production
          if (process.env.NODE_ENV !== 'production') {
            debugLog('🔍 useAuthGuard Debug:', {
              pathname,
              requireAuth,
              redirectTo,
              redirectIfAuthenticated,
              testingMode: isTestingMode(),
              configChanged
            });
          }

          // In testing mode, bypass all authentication checks
          if (isTestingMode()) {
            if (process.env.NODE_ENV !== 'production') {
              debugLog('🧪 Testing mode: Bypassing auth guard for', pathname);
            }
            setIsAuthenticated(true); // Set as authenticated for testing
            setUser({ id: 'test-user', email: 'test@test.com', name: 'Test User' }); // Mock user
            setIsLoading(false);
            return;
          }

          // Production mode: normal authentication checks
          const authenticated = authService.isAuthenticated();
          const currentUser = authService.getCurrentUser();

          if (process.env.NODE_ENV !== 'production') {
            debugLog('🔍 Authentication Status:', {
              authenticated,
              hasUser: !!currentUser,
              requireAuth,
              redirectIfAuthenticated
            });
          }

          setIsAuthenticated(authenticated);
          setUser(currentUser);

          if (requireAuth && !authenticated) {
            if (process.env.NODE_ENV !== 'production') {
              debugLog(`❌ Access denied to ${pathname}. Redirecting to ${redirectTo}`);
            }
            router.replace(redirectTo);
            return;
          }

          if (redirectIfAuthenticated && authenticated) {
            // Do not redirect if on auth flow pages that require completion
            // EXCEPT for OTP page after successful verification - that should redirect
            const isOtpPage = pathname.startsWith('/OTP');
            const shouldSkipRedirect = !isOtpPage && (
              pathname.startsWith('/changePassword') || 
              pathname.startsWith('/createPassword') || 
              pathname.startsWith('/resetPassword') || 
              pathname.startsWith('/verifyAccount')
            );
            
            if (shouldSkipRedirect) {
              if (process.env.NODE_ENV !== 'production') {
                debugLog(`🔄 Skipping redirect for auth flow page: ${pathname}`);
              }
              return;
            }
            if (process.env.NODE_ENV !== 'production') {
              debugLog(`✅ User already authenticated on ${pathname}. Redirecting to ${redirectIfAuthenticated}`);
            }
            router.replace(redirectIfAuthenticated);
            return;
          }

          if (process.env.NODE_ENV !== 'production') {
            debugLog(`✅ Auth check passed for ${pathname}`);
          }

          // NOTE: Permission checks removed - now handled by ProtectedPage component with RBAC
          // Old permission system (requiredPermission) has been deprecated in favor of role-based access control

        } catch (error) {
          debugLog('❌ Error checking authentication:', error);
          if (requireAuth && !isTestingMode()) {
            router.replace(redirectTo);
          }
        } finally {
          if (!isTestingMode()) {
            setIsLoading(false);
          }
        }
      };

      checkAuth();
    }
  }, [requireAuth, redirectTo, redirectIfAuthenticated, router, pathname]);

  return {
    isAuthenticated,
    isLoading,
    user,
    // Deprecated: Use ProtectedPage component with RBAC instead
    hasPermission: () => true, // Always return true - permissions now handled by RBAC
  };
};

/**
 * Higher-order component for authentication protection
 * @param {React.Component} WrappedComponent - Component to protect
 * @param {Object} options - Authentication options
 */
export const withAuthGuard = (WrappedComponent, options = {}) => {
  return function AuthGuardedComponent(props) {
    const { isAuthenticated, isLoading, user } = useAuthGuard(options);

    // In testing mode, always render the component immediately
    if (isTestingMode()) {
      return <WrappedComponent {...props} user={user} isAuthenticated={true} />;
    }

    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen w-full bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-inversePurple-100 mx-auto mb-4"></div>
            <p className="text-lg text-gray-600">
              {options.requireAuth ? 'Checking authentication...' : 'Loading...'}
            </p>
          </div>
        </div>
      );
    }

    // Don't render if auth is required but user is not authenticated
    if (options.requireAuth !== false && !isAuthenticated) {
      return null; // Router will handle redirect
    }

    // Don't render if user is authenticated but shouldn't be (login page)
    if (options.redirectIfAuthenticated && isAuthenticated) {
      return null; // Router will handle redirect
    }

    return <WrappedComponent {...props} user={user} isAuthenticated={isAuthenticated} />;
  };
};

/**
 * Authentication loading component
 */
export const AuthLoadingScreen = ({ message = 'Checking authentication...' }) => (
  <div className="flex items-center justify-center min-h-screen w-full bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-inversePurple-100 mx-auto mb-4"></div>
      <p className="text-lg text-gray-600">{message}</p>
    </div>
  </div>
);

export default useAuthGuard; 